import React, { useState, useRef } from "react";
import PropTypes from "prop-types";
import styles from "./Checkbox.module.css";
import { useEffect } from "react";

/**
 * CheckboxGroup
 * @type React.FC<CheckboxGroupPropTypes>
 */
const CheckboxGroup = React.forwardRef((props, ref) => {
  Object.keys(props).forEach((p) => {
    if (!Object.keys(CheckboxGroupPropTypes).includes(p)) {
      console.error(
        `CheckboxGroup: 정의되지 않은 prop ${p}가 전달되었습니다.`
      );
    }
  });

  const refInit = useRef([]);

  /**
   * CheckboxGroup Component의 State
   */
  const initState = props.dataSource.map((x, idx) => ({
    textLabel: x.textLabel ?? props.textLabel + (idx + 1),
    id: x.id ?? props.textLabel + +(idx + 1),
    disabled: x.disabled ?? false,
    checked: x.defaultChecked ?? false,
    value: x.value ?? props.defaultValue,
    img: x.img ?? null,
    alt: x.alt ?? "",
  }));

  const [currState, setCurrState] = useState(initState);

  /*
   * row 당 요청된 props.numOfCols 값만큼 배열에 담아서 출력하기 위한 함수
   */
  const rowGroup = currState.reduce((accumulator, currentValue, index) => {
    const grpIndex = Math.floor(index / Math.floor(props.numOfCols));

    if (!accumulator[grpIndex]) accumulator[grpIndex] = [];
    accumulator[grpIndex].push(currentValue);

    return accumulator;
  }, []);

  useEffect(() => {
    if (props.value) {
      // props.value는 [ checkedItem1, checkedItem2, … ] 형태라고 가정
      setCurrState((prev) =>
        prev.map((item, idx) => ({
          ...item,
          checked: props.value[idx]?.checked,
        }))
      );
    }
  }, [props.value]);
  useEffect(() => {
    if (!refInit.current?.length && rowGroup) refInit.current = rowGroup.flat();
  }, [rowGroup]);

  /** 외부에서 접근할 수 있는 함수들 */
  React.useImperativeHandle(ref, () => ({
    /** 값 가져오기 */
    getValue: () => {
      return currState
        .filter((x) => {
          return x.checked;
        })
        .map((x) => x.value);
    },
    /** checked 여부 가져오기 */
    getChecked: () => {
      return currState.filter((x) => {
        return x.checked;
      });
    },
    /** checked 상태 변경 */
    setAllChecked: (chk) => {
      setCurrState((x) =>
        x.map((prev) => ({
          ...prev,
          checked: chk,
        }))
      );
    },
    /** return checked : true, false */
    getStateArray: () => {
      return currState.map((x) => {
        return x.checked;
      });
    },
    /** checkbox enable */
    enable: (id) => {
      _chageDisable(id, false);
    },
    /** checkbox disable */
    disable: (id) => {
      _chageDisable(id, true);
    },
    setAllDisable: () => {
      setCurrState((x) =>
        x.map((prev) => ({
          ...prev,
          disabled: true,
        }))
      );
    },
    setAllEnable: () => {
      setCurrState((x) =>
        x.map((prev) => ({
          ...prev,
          disabled: false,
        }))
      );
    },
    setStateArray: (i, chk) => {
      setCurrState((x) =>
        x.map((prev, idx) => ({
          ...prev,
          checked: i === idx ? chk : prev.checked,
        }))
      );
      return currState.map((x, idx) => {
        return x.checked;
      });
    },
    getValues: () => {
      return currState;
    },
    clear() {
      setCurrState(refInit.current);
    },
    /** checkbox data setting */
    setDataSource: (datasource) => {
      const data = datasource.map((x, idx) => ({
        textLabel: x.textLabel ?? x[props.textLabel] + (idx + 1),
        id: x.id ?? x[props.id],
        disabled: x.disabled ?? false,
        checked: x.defaultChecked ?? false,
        value: x.value ?? props.defaultValue,
        img: x.img ?? null,
        alt: x.alt ?? "",
        desc: x[props.desc] ?? "",
      }));

      setCurrState(data);
    },
    updateState(stateName, newValue) {
      // currState에 있는 값만 업데이트 가능
      if (Object.keys(currState[0]).includes(stateName)) {
        setCurrState((x) =>
          x.map((prev) => ({
            ...prev,
            [stateName]: newValue,
          }))
        );
      } else {
        console.error(
          "CheckboxGroup: State에 지정되지 않은 값을 업데이트 시도하였습니다. 잘못된 시도입니다."
        );
      }
    },
    setStateValues: (state) => {
      setCurrState(state);
    },
  }));

  /**
   * 변경 이벤트
   */
  const _onChange = (e, index) => {
    setCurrState((x) => {
      const updatedState = x.map((prev, idx) => ({
        ...prev,
        checked: index === idx ? !prev.checked : prev.checked,
      }));

      if (props.onChange)
        props.onChange({ e, selectedIndex: index }, updatedState);

      return updatedState;
    });
  };

  /**
   * id 또는 index 기준 체크박스 enable,disable
   * n번째 요소 id와 n+1번째 요소 index의 값이 같다면 n번째 요소가 선택됨
   */
  const _chageDisable = (id, NewValue) => {
    const item = currState.find((item, idx) => item.id === id || idx === id);

    if (item) {
      setCurrState((x) =>
        x.map((prev) => ({
          ...prev,
          disabled: prev.id === item.id ? NewValue : prev.disabled,
        }))
      );
    } else {
      console.error(
        "CheckboxGroup: 지정되지 않은 값을 시도하였습니다. 잘못된 시도입니다."
      );
    }
  };

  // css가 제대로 먹히지 않아 분리하여 생성하도록 수정
  return props.imageValue ? (
    <>
      {rowGroup.map((row, parentIdx) => (
        <div
          key={parentIdx}
          style={{
            display: "flex",
            gap: "25px",
            width: "100%",
            justifyContent: "center",
          }}
        >
          {" "}
          {/* 이미지 일경우 가운데 정렬 추가됨  */}
          {row.map((item, childIdx) => (
            <div
              key={childIdx}
              style={{
                display: "flex",
                alignItems: "center",
                flexDirection: "column",
                gap: "10px",
                padding: "5px",
              }}
            >
              {item.img !== null && (
                <img
                  src={import.meta.env.VITE_PUBLIC_URL + item.img}
                  alt={item.alt}
                  width={"70px"}
                />
              )}
              <label className={styles.label}>
                <input
                  className={styles.checkbox}
                  type={"checkbox"}
                  id={item.id}
                  name={item.textLabel}
                  checked={item.checked}
                  onChange={(e) =>
                    _onChange(e, parentIdx * props.numOfCols + childIdx)
                  }
                  value={item.value}
                  disabled={item.disabled}
                />
                <p className={styles.labelText}>{item.textLabel}</p>
              </label>
            </div>
          ))}
        </div>
      ))}
    </>
  ) : (
    <>
      {rowGroup.map((row, parentIdx) => (
        <div
          key={parentIdx}
          style={{ display: "flex", gap: "10px 15px", flexWrap: "wrap" }}
        >
          {row.map((item, childIdx) => (
            <div
              key={childIdx}
              style={{ display: "flex", alignItems: "center" }}
            >
              <label className={styles.label}>
                <input
                  className={styles.checkbox}
                  type={"checkbox"}
                  id={item.id}
                  name={item.textLabel}
                  checked={item.checked}
                  onChange={(e) =>
                    _onChange(e, parentIdx * props.numOfCols + childIdx)
                  }
                  value={item.value}
                  disabled={item.disabled}
                />
                <p className={styles.labelText}>{item.textLabel}</p>
                {item?.desc ? <span>{item.desc}</span> : null}
              </label>
            </div>
          ))}
        </div>
      ))}
    </>
  );
});

const CheckboxGroupPropTypes = {
  /**
   * 텍스트. 비어있는 경우 width를 차지 하지 않음
   * updateState('textLabel', string) 함수로 수정 가능
   */
  textLabel: PropTypes.string,
  /**
   * disabled: 비활성화
   * 기본값 false
   * updateState('disabled', bool) 함수로 수정 가능
   */
  disabled: PropTypes.bool,
  /**
   * 최초 check 값
   * setChecked(bool)로 변경
   * getChecked()로 데이터 참조
   */
  defaultChecked: PropTypes.bool,
  /**
   * 최초 value 값 (checked와 별개)
   * setValue(string)로 변경
   * getValue()로 데이터 참조
   */
  defaultValue: PropTypes.string,
  /**
   * 변경 이벤트
   */
  onChange: PropTypes.func,
  id: PropTypes.string,
  dataSource: PropTypes.array,

  /**
   * numOfCols: 체크박스 가로 개수
   * 기본값 0
   */
  numOfCols: PropTypes.number,

  imageValue: PropTypes.bool,
  value: PropTypes.array,
  desc: PropTypes.string,
};

CheckboxGroup.propTypes = CheckboxGroupPropTypes;
CheckboxGroup.defaultProps = {
  id: "chk",
  textLabel: "라벨",
  disabled: false,
  defaultChecked: false,
  defaultValue: "on",
  onChange: null,
  dataSource: [],
  numOfCols: 1,
  imageValue: false,
  value: [],
  desc: null,
};
CheckboxGroup.displayName = "CheckboxGroup";
export default CheckboxGroup;
